import React, { useState, useRef, useEffect } from 'react';
import { MessageSquarePlus, Send, Loader2, BookA, Languages, Sparkles, MessageCircle, AlertCircle, GraduationCap, Copy, Check, Image, Settings, Share2, RotateCcw, Trash2, Square, SpellCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { domToPng } from 'modern-screenshot';
import Dropdown from '../Dropdown';
import MellowMarkdown from '../MellowMarkdown';
import StreamingMellowMessage from '../StreamingMellowMessage';
import WelcomeSuggestions from '../WelcomeSuggestions';
import SelectionPopover from '../SelectionPopover';
import UsageModal from '../UsageModal';
import CourseConfigModal from '../CourseConfigModal';
import ShareChatModal from '../ShareChatModal';
import IconButton from '../IconButton';
import { useChat } from '../../context/ChatContext';
import { STREAMING_STATES } from '../../hooks/useStreamingStateMachine';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { useChatScrollHide } from '../../hooks/useChatScrollHide';
import { useSelector } from '@legendapp/state/react';
import { languages$, selectLanguage } from '../../store/languages$';
import { LANGUAGE_OPTIONS } from '../AddLanguageModal';
import { pinChat, deleteChat } from '../../store/chats$';
import DropdownMenu from '../DropdownMenu';
import './ChatTab.css';

export default function ChatTab({ tabMode = 'query' }) {
  const { t } = useTranslation(['chat', 'common', 'languages', 'languageCodes']);
  const {
    messages,
    messageSources,
    sending,
    error,
    isLoading,
    activeChatId,
    activeChat,
    chatOptions,
    sendMessage,
    startNewChat,
    selectChat,
    selectedLanguage,
    lookupDictionary,
    lookupGrammar,
    lookupNative,
    lookupCorrection,
    startCourse,
    // Welcome state
    welcomeContent,
    welcomeLoading,
    welcomeError,
    fetchWelcome,
    markWelcomeUsedAndPrefetch,
    isOnNewChatScreen,
    // Credits
    insufficientCredits,
    clearInsufficientCredits,
    // Pending input (from Discovery "Ask AI")
    pendingInput,
    setPendingInput,
    // Message actions
    retryFromMessage,
    deleteFromMessage,
    // Streaming
    streamingState,
    streamingMessageIndex,
    isStreamingActive,
    forceCompleteStreaming,
    handleAnimationComplete,
    stopStreaming,
    streamBufferRef,
    visibleLengthRef,
    cleanupStreaming,
  } = useChat();

  // Abort any in-flight streaming when this tab unmounts (tab switch).
  // With Plan A, onDone already wrote to Supabase, so this only needs to handle
  // mid-flight abort (onAbort writes partial content) and state machine reset.
  useEffect(() => {
    return () => { cleanupStreaming(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Language switcher state
  const userLanguages = useSelector(() => languages$.items.get());
  const currentLanguageCode = useSelector(() => languages$.selectedCode.get());
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const langButtonRef = useRef(null);

  const [inputValue, setInputValue] = useState('');
  const defaultMode = tabMode === 'course' ? 'course' : 'dict';
  const [inputMode, setInputMode] = useState(
    () => localStorage.getItem(`mellow_inputMode_${tabMode}`) || defaultMode
  );
  useEffect(() => {
    localStorage.setItem(`mellow_inputMode_${tabMode}`, inputMode);
  }, [inputMode, tabMode]);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [exportingMessageIndex, setExportingMessageIndex] = useState(null);
  const [copiedUserMessageIndex, setCopiedUserMessageIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const messageRefs = useRef({});

  // Scroll-to-hide bottom chrome (input + nav) on mobile
  useChatScrollHide({ containerRef: messagesAreaRef, sending });

  // Credits
  const { balance, loading: balanceLoading } = useCreditBalance();
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

  // Course config modal
  const [isCourseConfigOpen, setIsCourseConfigOpen] = useState(false);

  // Share modal
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Prepare language dropdown options
  const languageDropdownOptions = userLanguages.map(lang => {
    const langInfo = LANGUAGE_OPTIONS.find(l => l.value === lang.language_code);
    return {
      value: lang.language_code,
      label: t(`languages:${lang.language_code}`),
      icon: langInfo?.icon || '🌐',
    };
  });

  // Handle language change
  const handleLanguageChange = async (langCode) => {
    if (langCode === currentLanguageCode) {
      setShowLangDropdown(false);
      return;
    }

    // Select the new language (this will trigger chat context to refresh)
    await selectLanguage(langCode);
    setShowLangDropdown(false);

    // Start a new chat for the new language (chat context will handle this automatically)
    // The useChat hook watches selectedLanguage and will refresh welcome content
  };

  // Get current language short code for the button (i18n localized)
  const currentShortCode = currentLanguageCode
    ? t(`languageCodes:${currentLanguageCode}`, currentLanguageCode.split('-')[0].toUpperCase().slice(0, 3))
    : t('languageCodes:en', 'EN');
  const showLanguageSwitcher = userLanguages.length > 1;

  // Input mode configuration (using translations)
  const INPUT_MODES = {
    dict: {
      label: t('chat:input.mode.dict.label'),
      icon: BookA,
      placeholder: t('chat:input.mode.dict.placeholder'),
      color: '#3498db',
    },
    grammar: {
      label: t('chat:input.mode.grammar.label'),
      icon: Languages,
      placeholder: t('chat:input.mode.grammar.placeholder'),
      color: '#9b59b6',
    },
    native: {
      label: t('chat:input.mode.native.label'),
      icon: Sparkles,
      placeholder: t('chat:input.mode.native.placeholder'),
      color: '#f5a623',
    },
    correction: {
      label: t('chat:input.mode.correction.label'),
      icon: SpellCheck,
      placeholder: t('chat:input.mode.correction.placeholder'),
      color: '#FF7675',
    },
    ask: {
      label: t('chat:input.mode.ask.label'),
      icon: MessageCircle,
      placeholder: t('chat:input.mode.ask.placeholder'),
      color: '#666',
    },
    course: {
      label: t('chat:input.mode.course.label'),
      icon: GraduationCap,
      placeholder: t('chat:input.mode.course.placeholder'),
      color: '#8B5CF6',
    },
  };

  // Filter modes based on tabMode
  const QUERY_MODES = ['dict', 'grammar', 'native', 'correction', 'ask'];
  const allowedModeKeys = tabMode === 'course' ? ['course'] : QUERY_MODES;
  const visibleModes = Object.entries(INPUT_MODES).filter(([mode]) => allowedModeKeys.includes(mode));

  // Clear active chat on mount if it doesn't belong to this tab's mode
  useEffect(() => {
    if (!activeChat) return;
    const isCourseChat = activeChat.initial_mode === 'course';
    if (tabMode === 'course' && !isCourseChat) {
      selectChat(null);
    } else if (tabMode === 'query' && isCourseChat) {
      selectChat(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Fetch welcome when chat is empty and no welcome content yet
  useEffect(() => {
    if (messages.length === 0 && !welcomeContent && !welcomeLoading && !isLoading && selectedLanguage) {
      fetchWelcome();
    }
  }, [messages.length, welcomeContent, welcomeLoading, isLoading, selectedLanguage, fetchWelcome]);

  // Consume pending input from other tabs (e.g., Discovery "Ask AI")
  useEffect(() => {
    if (pendingInput) {
      setInputValue(pendingInput);
      setPendingInput(null); // Clear after consuming
    }
  }, [pendingInput, setPendingInput]);

  // Scroll to last user message after AI response (not to bottom)
  useEffect(() => {
    // When sending stops (AI just responded), scroll to user's question
    if (!sending && messages.length > 0) {
      // Find the last user message index
      const lastUserIdx = messages.findLastIndex(m => m.role === 'user');
      if (lastUserIdx >= 0) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const userMsgEl = document.querySelector(`[data-msg-idx="${lastUserIdx}"]`);
          if (userMsgEl) {
            userMsgEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        }, 100);
      }
    }
  }, [messages, sending]);

  // Watch for insufficient credits and open modal
  useEffect(() => {
    if (insufficientCredits) {
      setIsUsageModalOpen(true);
      clearInsufficientCredits();
    }
  }, [insufficientCredits, clearInsufficientCredits]);

  // Reset inputMode to default if current mode doesn't belong to this tab
  useEffect(() => {
    if (tabMode === 'query' && inputMode === 'course' && !isOnNewChatScreen && activeChat?.initial_mode !== 'course') {
      setInputMode('dict');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnNewChatScreen, activeChat?.initial_mode]); // Intentionally exclude inputMode to avoid cascading

  // Handle send based on input mode
  const handleSend = () => {
    if (!inputValue.trim() || sending) return;

    const text = inputValue.trim();

    // If on new chat screen (welcome showing), mark as used and prefetch
    if (isOnNewChatScreen) {
      markWelcomeUsedAndPrefetch();
    }

    switch (inputMode) {
      case 'dict':
        lookupDictionary(text);
        break;
      case 'grammar':
        lookupGrammar(text);
        break;
      case 'native':
        lookupNative(text);
        break;
      case 'correction':
        lookupCorrection(text);
        break;
      case 'course':
        startCourse(text);
        break;
      default:
        sendMessage(text);
    }

    setInputValue('');
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Block only while the network fetch is active; animation phases don't block input
      if (!sending && streamingState !== STREAMING_STATES.NETWORK_STREAMING) {
        handleSend();
      }
    }
  };

  // Handle copy AI message
  const handleCopyMessage = async (content, messageIndex) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(messageIndex);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle copy user message
  const handleCopyUserMessage = async (content, messageIndex) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedUserMessageIndex(messageIndex);
      setTimeout(() => setCopiedUserMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle delete user message
  const handleDeleteUserMessage = (messageIndex) => {
    deleteFromMessage(messageIndex);
  };

  // Handle retry AI message
  const handleRetryMessage = (messageIndex) => {
    retryFromMessage(messageIndex);
  };

  // Handle pin chat
  const handlePinChat = async (chatId) => {
    try {
      await pinChat(chatId);
    } catch (err) {
      console.error('Failed to pin chat:', err);
    }
  };

  // Handle delete chat
  const handleDeleteChat = async (chatId) => {
    const confirmed = window.confirm('Delete this conversation? This cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteChat(chatId);
    } catch (err) {
      console.error('Failed to delete chat:', err);
    }
  };

  // Handle export message to image
  const handleExportMessage = async (messageIndex) => {
    const messageElement = messageRefs.current[messageIndex];
    if (!messageElement) {
      console.error('Message element not found');
      return;
    }

    try {
      setExportingMessageIndex(messageIndex);

      // Generate PNG with 2x scale for high quality
      // Add 5px extra white padding on the left
      const dataUrl = await domToPng(messageElement, {
        quality: 1,
        scale: 2,
        backgroundColor: '#ffffff',
        style: {
          padding: '35px 20px 20px 20px',
        },
      });

      // Download the image
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `mellow-chat-${timestamp}.png`;
      link.href = dataUrl;
      link.click();

      // Reset state after a short delay
      setTimeout(() => setExportingMessageIndex(null), 1000);
    } catch (err) {
      console.error('Failed to export message:', err);
      setExportingMessageIndex(null);
    }
  };

  // Render AI message content with markdown
  const renderAIContent = (content, messageIndex) => {
    return (
      <MellowMarkdown
        chatId={activeChatId}
        messageIndex={messageIndex}
        languageCode={selectedLanguage}
        initialMode={activeChat?.initial_mode}
      >
        {content}
      </MellowMarkdown>
    );
  };

  // Enhance chat options with leftIcon for course chats
  const enhancedChatOptions = chatOptions.map(option => ({
    ...option,
    leftIcon: option.initialMode === 'course' ? <GraduationCap size={16} /> : null,
  }));

  // Filter chat dropdown: query tab shows non-course chats, course tab shows course chats
  const filteredChatOptions = enhancedChatOptions.filter(option =>
    tabMode === 'course'
      ? option.initialMode === 'course'
      : option.initialMode !== 'course'
  );

  return (
    <div className="chat-container">
      {/* Header */}
      <header className="chat-header">
        <div className="chat-title-dropdown">
          <Dropdown
            value={activeChatId}
            onChange={selectChat}
            options={filteredChatOptions}
            placeholder={t('chat:newChat')}
            maxHeight="calc(100vh - 220px)"
            onPin={handlePinChat}
            onDelete={handleDeleteChat}
          />
        </div>
        <div className="chat-header-actions">
          {/* Course config - only in course tab */}
          {tabMode === 'course' && (
            <button
              className="course-config-btn"
              onClick={() => setIsCourseConfigOpen(true)}
              title="Course Preferences"
              style={{ padding: '4px' }}
            >
              <Settings size={20} strokeWidth={1.5} />
            </button>
          )}
          {/* Share button - only show when in an existing chat with messages */}
          {activeChatId && messages.length > 0 && (
            <button
              className="share-chat-btn"
              onClick={() => setIsShareModalOpen(true)}
              title={t('chat:share.title', 'Share conversation')}
            >
              <Share2 size={20} strokeWidth={1.5} />
            </button>
          )}
          <button className="new-chat-btn" onClick={() => { startNewChat(); setInputMode(defaultMode); }}>
            <MessageSquarePlus size={24} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Messages */}
      <div className="messages-area no-scrollbar" ref={messagesAreaRef}>
        {/* Selection popover for Dict/Grammar lookup */}
        <SelectionPopover containerRef={messagesAreaRef} />

        {isLoading ? (
          <div className="chat-loading">
            <Loader2 className="spin" size={24} />
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-welcome">
            {welcomeLoading ? (
              <div className="welcome-loading">
                <Loader2 className="spin" size={20} />
                <span>{t('chat:message.wakingUp')}</span>
              </div>
            ) : welcomeError ? (
              <div className="welcome-error">
                <p>Hi! What would you like to learn today?</p>
                <button className="retry-btn" onClick={fetchWelcome}>
                  Try again
                </button>
              </div>
            ) : welcomeContent ? (
              <WelcomeSuggestions content={welcomeContent} tabMode={tabMode} />
            ) : (
              <div className="welcome-loading">
                <Loader2 className="spin" size={20} />
                <span>Mellow is waking up...</span>
              </div>
            )}
          </div>
        ) : (
          messages.map((msg, idx) => {
            // Check if this is a dict/grammar/native message (user messages only)
            const source = msg.role === 'user' ? messageSources?.[idx] : null;
            const isDictMessage = source?.type === 'dictionary';
            const isGrammarMessage = source?.type === 'grammar';
            const isNativeMessage = source?.type === 'native';
            const isCorrectionMessage = source?.type === 'correction';

            if (msg.role === 'user') {
              // Render capsule for dict/grammar/native, else normal bubble
              if (isDictMessage) {
                return (
                  <div key={idx} data-msg-idx={idx} className="message-row user">
                    <div className="mellow-capsule dict-capsule">
                      <BookA size={14} />
                      <span>{t('chat:message.lookupPrefix', { text: source.quoteText })}</span>
                    </div>
                  </div>
                );
              }
              if (isGrammarMessage) {
                return (
                  <div key={idx} data-msg-idx={idx} className="message-row user">
                    <div className="mellow-capsule grammar-capsule">
                      <Languages size={14} />
                      <span>{t('chat:message.grammarPrefix', { text: source.quoteText.length > 50 ? source.quoteText.slice(0, 50) + '...' : source.quoteText })}</span>
                    </div>
                  </div>
                );
              }
              if (isNativeMessage) {
                return (
                  <div key={idx} data-msg-idx={idx} className="message-row user">
                    <div className="mellow-capsule native-capsule">
                      <Sparkles size={14} />
                      <span>{t('chat:message.nativePrefix', { text: source.quoteText.length > 50 ? source.quoteText.slice(0, 50) + '...' : source.quoteText })}</span>
                    </div>
                  </div>
                );
              }
              if (isCorrectionMessage) {
                return (
                  <div key={idx} data-msg-idx={idx} className="message-row user">
                    <div className="mellow-capsule correction-capsule">
                      <SpellCheck size={14} />
                      <span>{t('chat:message.correctionPrefix', { text: source.quoteText.length > 50 ? source.quoteText.slice(0, 50) + '...' : source.quoteText })}</span>
                    </div>
                  </div>
                );
              }

              // Regular user message bubble
              // Check if this is the first user message (starts conversation)
              const isFirstUserMessage = messages.slice(0, idx).filter(m => m.role === 'user').length === 0;

              return (
                <div key={idx} data-msg-idx={idx} className="message-row user">
                  <div className="user-message-wrapper">
                    <div className="message-bubble user">{msg.content}</div>
                    {/* Show actions only for non-first user messages */}
                    {!isFirstUserMessage && (
                      <div className="user-message-actions">
                        <IconButton
                          onClick={() => handleCopyUserMessage(msg.content, idx)}
                          title={copiedUserMessageIndex === idx ? t('chat:message.copied') : t('chat:message.copy')}
                          className="copy-user-message-btn"
                        >
                          {copiedUserMessageIndex === idx ? <Check size={16} /> : <Copy size={16} />}
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteUserMessage(idx)}
                          title={t('chat:message.delete')}
                          className="delete-user-message-btn"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            // AI message
            const isThisMessageStreaming = streamingMessageIndex === idx && isStreamingActive;
            return (
              <div
                key={idx}
                className="message-stream ai"
                ref={(el) => { messageRefs.current[idx] = el; }}
              >
                {isThisMessageStreaming ? (
                  <StreamingMellowMessage
                    text={msg.content}
                    isStreaming={streamingState === STREAMING_STATES.NETWORK_STREAMING}
                    streamBufferRef={streamBufferRef}
                    visibleLengthRef={visibleLengthRef}
                    onAnimationComplete={() => handleAnimationComplete(idx)}
                    forceComplete={forceCompleteStreaming}
                    chatId={activeChatId}
                    messageIndex={idx}
                    languageCode={selectedLanguage}
                    initialMode={activeChat?.initial_mode}
                  />
                ) : (
                  renderAIContent(msg.content, idx)
                )}
                {/* Action buttons hidden while streaming this message */}
                {!isThisMessageStreaming && (
                  <div className="ai-message-actions">
                    <IconButton
                      onClick={() => handleRetryMessage(idx)}
                      title={t('chat:message.retry')}
                      className="retry-message-btn"
                    >
                      <RotateCcw size={16} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleCopyMessage(msg.content, idx)}
                      title={copiedMessageIndex === idx ? t('chat:message.copied') : t('chat:message.copy')}
                      className="copy-message-btn"
                    >
                      {copiedMessageIndex === idx ? <Check size={16} /> : <Copy size={16} />}
                    </IconButton>
                    <IconButton
                      onClick={() => handleExportMessage(idx)}
                      title={exportingMessageIndex === idx ? t('chat:message.exporting') : t('chat:message.exportImage')}
                      className="export-message-btn"
                      disabled={exportingMessageIndex === idx}
                    >
                      {exportingMessageIndex === idx ? <Loader2 className="spin" size={16} /> : <Image size={16} />}
                    </IconButton>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Sending indicator */}
        {sending && (
          <div className="message-stream ai">
            <div className="sending-indicator">
              <Loader2 className="spin" size={16} />
              <span>{t('chat:message.thinking')}</span>
            </div>
            {/* Course generation tip - only show on first response in course mode */}
            {activeChat?.initial_mode === 'course' && messages.length === 1 && (
              <p className="course-generation-tip">
                {t('chat:message.firstCourseGenerationTip')}
              </p>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="chat-error">
            <p>{t('chat:message.error', { message: error })}</p>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="input-area">
        {/* Low Credits Warning */}
        {!balanceLoading && balance !== null && balance < 100 && (
          <div className="low-credits-warning">
            <div className="warning-content">
              <AlertCircle size={18} />
              <div className="warning-text">
                <span className="warning-message">{t('chat:credits.lowBalance')}</span>
                <span className="warning-balance">{t('chat:credits.creditsLeft', { count: balance?.toLocaleString() || 0 })}</span>
              </div>
            </div>
            <button
              className="top-up-btn"
              onClick={() => setIsUsageModalOpen(true)}
            >
              {t('chat:credits.topUp')}
            </button>
          </div>
        )}

        {/* Mode Tabs - hidden when only one mode (course tab) */}
        {visibleModes.length > 1 && (
          <div className="input-mode-tabs">
            {visibleModes.map(([mode, config]) => {
              const Icon = config.icon;
              const isActive = inputMode === mode;

              return (
                <button
                  key={mode}
                  className={`input-mode-tab ${isActive ? 'active' : ''}`}
                  onClick={() => setInputMode(mode)}
                  style={{
                    '--tab-color': config.color,
                  }}
                >
                  <Icon size={14} />
                  <span>{config.label}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="input-box-wrapper" style={{
          '--active-color': INPUT_MODES[inputMode].color,
        }}>
          {/* Language switcher button - only show if user has multiple languages */}
          {showLanguageSwitcher && (
            <button
              ref={langButtonRef}
              className="lang-switcher-btn"
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              title={t('chat:input.switchLanguage', 'Switch learning language')}
            >
              <span className="lang-code">{currentShortCode}</span>
            </button>
          )}

          <input
            type="text"
            className="input-box"
            placeholder={INPUT_MODES[inputMode].placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="send-btn"
            onClick={(sending || streamingState === STREAMING_STATES.NETWORK_STREAMING) ? stopStreaming : handleSend}
            disabled={!(sending || streamingState === STREAMING_STATES.NETWORK_STREAMING) && !inputValue.trim()}
            style={{
              '--btn-color': INPUT_MODES[inputMode].color,
            }}
          >
            {(sending || streamingState === STREAMING_STATES.NETWORK_STREAMING) ? <Square size={20} fill="currentColor" /> : <Send size={20} />}
          </button>
        </div>
      </div>

      {/* Language Dropdown */}
      {showLanguageSwitcher && (
        <DropdownMenu
          isOpen={showLangDropdown}
          onClose={() => setShowLangDropdown(false)}
          options={languageDropdownOptions}
          value={currentLanguageCode}
          onChange={handleLanguageChange}
          triggerRef={langButtonRef}
          showCheck={true}
          minWidth={180}
          title={t('chat:input.learningLanguages', 'Learning Languages')}
        />
      )}

      {/* Usage Modal */}
      <UsageModal
        isOpen={isUsageModalOpen}
        onClose={() => setIsUsageModalOpen(false)}
      />

      {/* Course Config Modal */}
      <CourseConfigModal
        isOpen={isCourseConfigOpen}
        onClose={() => setIsCourseConfigOpen(false)}
      />

      {/* Share Chat Modal */}
      <ShareChatModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        chatId={activeChatId}
      />
    </div>
  );
}
